import { fetchFeaturesFamilyMembersOrThrow, fetchPangenomeAlignmentMembersOrThrow } from "../api/fetch.ts";
import { refreshTracks } from "../main-canvas/messages/post-track-messages.ts";
import { PileupRenderingModeEnum, SyntenyTypeEnum } from "../openapi";
import { type GenomeViewerState, useGenomeViewerStore } from "../store/store.ts";
import type { MiniFamily } from "../synteny-worker/state.ts";
import { type MinimalItem } from "../types.ts";
import { computeSyntenyTrackAlignments } from "./tracks/track-alignment.ts";
import { calculateContigSwaps } from "./tracks/track-groups-with-feature-elsewhere.ts";

export interface MemberItem extends MinimalItem {
  trackGroupId: string;
  familyId: number;
  contigId: number;
  start?: number;
}

export async function align(to: MemberItem, options?: { forceCenter?: boolean }) {
  const {
    tracks: _tracks,
    trackGroups: _groups,
    activeSyntenyType,
    updateTrackContigId,
    pileupRenderingMode,
    alignedFamily,
    syntenyOrganism,
  } = useGenomeViewerStore.getState();

  const trackType = _tracks.get(to.trackId)?.trackType;

  if (!activeSyntenyType || !trackType || !syntenyOrganism.id) {
    return;
  }

  const condense = pileupRenderingMode === PileupRenderingModeEnum.CONDENSED;
  const familyMembers =
    activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT
      ? await fetchPangenomeAlignmentMembersOrThrow(syntenyOrganism.id, to.familyId)
      : await fetchFeaturesFamilyMembersOrThrow(to.familyId, activeSyntenyType, condense);
  const newTrackGroups = calculateContigSwaps(to, _groups, familyMembers, alignedFamily, _tracks);

  for (const { groupId, newContigId, toBase } of newTrackGroups) {
    updateTrackContigId(groupId, newContigId, toBase);
  }

  const { trackGroups, tracks } = useGenomeViewerStore.getState();
  const updatedAlignedFamily = updateAlignedFamilyMember(to, alignedFamily);

  const { newAlignedFamily, newAlignedTrackGroupIds } = computeSyntenyTrackAlignments({
    fromMemberItem: to,
    trackType,
    trackGroups,
    tracks,
    alignedFamily: updatedAlignedFamily,
    newTrackGroups,
    familyMembers,
  });

  const storeUpdates: Partial<GenomeViewerState> = {
    alignedFamily: newAlignedFamily,
    currentlyAlignedTrackGroupIds: newAlignedTrackGroupIds,
    activeMemberId: to.featureId,
  };

  useGenomeViewerStore.setState(storeUpdates);
  refreshTracks();

  if (options?.forceCenter) {
    useGenomeViewerStore.getState().centerAlignment();
  }
}

function updateAlignedFamilyMember(to: MemberItem, alignedFamily: MiniFamily | undefined): MiniFamily {
  const newMember: MinimalItem = {
    featureId: to.featureId,
    trackId: to.trackId,
  };

  if (!alignedFamily) {
    return {
      familyId: to.familyId,
      members: [newMember],
    };
  }

  const isSameFamily = alignedFamily.familyId === to.familyId;

  if (isSameFamily) {
    const newAlignedFamily = { ...alignedFamily };
    const idx = newAlignedFamily.members.findIndex((member) => member.trackId === to.trackId);

    if (idx > -1) {
      newAlignedFamily.members[idx] = newMember;
    } else {
      newAlignedFamily.members.push(newMember);
    }

    return newAlignedFamily;
  } else {
    return {
      familyId: to.familyId,
      members: [newMember],
    };
  }
}

export const alignActiveFamily = () => {
  const { activeMemberId, alignedFamily, trackGroups, orthologousGroups } = useGenomeViewerStore.getState();
  // This util can only be called if there is already an aligned family existing
  if (!alignedFamily) return;

  // From the alignedFamily we get the OrthologData of each of its member, that contains more data (especially the start position)
  const activeMembers = orthologousGroups.find((orthoGroup) => orthoGroup.orthologId === alignedFamily.familyId);
  if (!activeMembers) return;

  // If the activeMemberId hasn't changed during a contig swap, get it's associated OrthologData. It will be used as the anchor member for aligning other trackGroups
  // Else set it to the first member of the activeMember
  const activeMember =
    activeMembers.orthologs.find((member) => member.id === activeMemberId) ?? activeMembers.orthologs[0];

  // This should never happen since the activeMembers.orthologs should contains at least 1 element
  if (!activeMember) return;

  // Updates the activeMemberId in the case it changed
  if (activeMember.id !== activeMemberId) {
    useGenomeViewerStore.setState({ activeMemberId: activeMember.id });
  }

  // activeBaseShift correspond to the baseShift of the trackGroup that contains the anchor member of the alignment
  const activeGroup = [...trackGroups.values()].find((group) => group.trackIds.includes(activeMember.trackId));
  if (!activeGroup) return;
  const activeBaseShift = activeGroup.graphics.baseShift;

  for (const member of alignedFamily.members) {
    // This converts the members of the aligned family ({id, trackId}) to OrthologData, containing especially the start position
    const familyMember = activeMembers.orthologs.find((ortho) => ortho.id === member.featureId);
    if (!familyMember) continue;

    if (familyMember.id !== activeMemberId) {
      const trackGroup = [...trackGroups.values()].find((group) => group.trackIds.includes(familyMember.trackId));
      if (!trackGroup) continue;

      // Apply to the trackGroup the new baseShift to align with the anchor member
      trackGroup.graphics.baseShift = activeBaseShift + activeMember.start - familyMember.start;
    }
  }

  useGenomeViewerStore.setState({ trackGroups });
  refreshTracks();
};
